import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Search, MapPin, Phone, Mail, ArrowUpDown, EyeOff, Droplets } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { NORWEGIAN_REGIONS } from '../lib/regions';
import CustomerFormFields from '../components/CustomerFormFields';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const getStatusBadge = (status) => {
  const badges = {
    overdue: { text: 'Forfalt', cls: 'bg-red-100 text-red-700' },
    due: { text: 'Denne uken', cls: 'bg-amber-100 text-amber-700' },
    upcoming: { text: 'Kommende', cls: 'bg-brand-100 text-brand-700' },
    scheduled: { text: 'Planlagt', cls: 'bg-slate-100 text-slate-600' },
    paused: { text: 'Pauset', cls: 'bg-slate-100 text-slate-600' },
    inactive: { text: 'Deaktivert', cls: 'bg-slate-100 text-slate-500' },
    no_cycle: { text: 'Ingen syklus', cls: 'bg-slate-100 text-slate-500' },
  };
  return badges[status] || badges.no_cycle;
};

export const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [cleaningMap, setCleaningMap] = useState({});
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('alphabetical');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    postal_code: '',
    region: '',
    notes: '',
    door_code: '',
    gate_code: '',
    alarm_code: '',
    access_info: '',
    cleaning_interval: '4'
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    let filtered = customers;
    
    // Filter active/inactive
    if (showInactive) {
      filtered = filtered.filter(c => c.is_active === false);
    } else {
      filtered = filtered.filter(c => c.is_active !== false);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.contact_person.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'alphabetical':
          return a.name.localeCompare(b.name, 'nb');
        case 'last_cleaned': {
          const ca = cleaningMap[a.id];
          const cb = cleaningMap[b.id];
          const va = ca ? ca.last_cleaned_year * 100 + ca.last_cleaned_week : -1;
          const vb = cb ? cb.last_cleaned_year * 100 + cb.last_cleaned_week : -1;
          return va - vb;
        }
        case 'due_soon': {
          const ca = cleaningMap[a.id];
          const cb = cleaningMap[b.id];
          const va = ca ? ca.next_due_year * 100 + ca.next_due_week : 999999;
          const vb = cb ? cb.next_due_year * 100 + cb.next_due_week : 999999;
          return va - vb;
        }
        default:
          return 0;
      }
    });
    
    setFilteredCustomers(sorted);
  }, [searchTerm, customers, sortBy, showInactive, cleaningMap]);

  const fetchCustomers = async () => {
    try {
      const [custRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/cleaning-cycles`)
      ]);
      setCustomers(custRes.data);
      setFilteredCustomers(custRes.data);
      const map = {};
      (cyclesRes.data || []).forEach(c => { map[c.customer_id] = c; });
      setCleaningMap(map);
    } catch (error) {
      toast.error('Kunne ikke hente kunder');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const { cleaning_interval, ...customerData } = newCustomer;
      const res = await axios.post(`${API}/customers`, customerData);
      const customerId = res.data.id;
      // Create cleaning cycle if interval is set
      if (cleaning_interval && parseInt(cleaning_interval) > 0) {
        await axios.put(`${API}/cleaning-cycles/${customerId}`, {
          interval_weeks: parseInt(cleaning_interval),
          is_active: true
        });
      }
      toast.success('Kunde opprettet!');
      setDialogOpen(false);
      setNewCustomer({ name: '', contact_person: '', phone: '', email: '', address: '', postal_code: '', region: '', notes: '', door_code: '', gate_code: '', alarm_code: '', access_info: '', cleaning_interval: '4' });
      fetchCustomers();
    } catch (error) {
      toast.error('Kunne ikke opprette kunde');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Kunder
          </h1>
          <p className="text-slate-600">{customers.filter(c => c.is_active !== false).length} aktive kunder</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" className="btn-primary bg-slate-900 hover:bg-slate-800">
              <Plus className="mr-2 h-4 w-4" />
              Ny Kunde
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Legg til ny kunde</DialogTitle>
              <DialogDescription>Fyll inn kundeinformasjon</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCustomer} className="space-y-4">
              <CustomerFormFields
                data={newCustomer}
                onChange={(field, value) => setNewCustomer({ ...newCustomer, [field]: value })}
                showCleaningInterval
              />
              <div className="space-y-2">
                <Label htmlFor="notes">Notater</Label>
                <Textarea
                  id="notes"
                  data-testid="notes-input"
                  value={newCustomer.notes || ''}
                  onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                  rows={2}
                  className="input-field"
                  placeholder="Notater om kunden..."
                />
              </div>
              <Button type="submit" data-testid="submit-customer-button" className="w-full btn-primary bg-slate-900 hover:bg-slate-800">
                Opprett Kunde
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-5 w-5" />
          <Input
            data-testid="search-customers-input"
            type="text"
            placeholder="Søk etter kunde..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 input-field"
          />
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="h-4 w-4 text-slate-400" />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[180px]" data-testid="sort-customers-select">
              <SelectValue placeholder="Sorter etter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Nyeste først</SelectItem>
              <SelectItem value="oldest">Eldste først</SelectItem>
              <SelectItem value="alphabetical">Alfabetisk</SelectItem>
              <SelectItem value="last_cleaned">Sist renset (eldst først)</SelectItem>
              <SelectItem value="due_soon">Neste rens (snarest)</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={showInactive ? "default" : "outline"}
            size="sm"
            data-testid="toggle-show-inactive"
            onClick={() => setShowInactive(!showInactive)}
            className={showInactive ? "bg-slate-700 hover:bg-slate-800 text-white" : "text-slate-500"}
          >
            <EyeOff className="h-4 w-4 mr-1.5" />
            Inaktive
          </Button>
        </div>
      </div>

      {filteredCustomers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-slate-600">Ingen kunder funnet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.map((customer) => (
            <Card
              key={customer.id}
              data-testid={`customer-card-${customer.id}`}
              className={`customer-card cursor-pointer ${customer.is_active === false ? 'opacity-60' : ''}`}
              onClick={() => navigate(`/customers/${customer.id}`)}
            >
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {customer.name}
                  </h3>
                  {customer.is_active === false && (
                    <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded-full" data-testid={`inactive-badge-${customer.id}`}>
                      Inaktiv
                    </span>
                  )}
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-slate-400" />
                    {customer.phone}
                  </div>
                  {customer.email && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-slate-400" />
                      {customer.email}
                    </div>
                  )}
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 mr-2 mt-0.5 text-slate-400 flex-shrink-0" />
                    <span className="break-words">{customer.address}</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs text-slate-500">
                    Kontakt: {customer.contact_person}
                  </p>
                  {(() => {
                    const cyc = cleaningMap[customer.id];
                    const badge = getStatusBadge(cyc?.status);
                    return (
                      <div className="mt-2 flex items-center justify-between gap-2" data-testid={`cleaning-info-${customer.id}`}>
                        <div className="flex items-center text-xs text-slate-500 min-w-0">
                          <Droplets className="h-3.5 w-3.5 mr-1.5 text-brand-500 shrink-0" />
                          <span className="truncate">
                            {cyc ? `Sist renset: Uke ${cyc.last_cleaned_week}, ${cyc.last_cleaned_year}` : 'Ingen rens registrert'}
                          </span>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${badge.cls}`}>{badge.text}</span>
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Customers;
